// LiveOps settings (spec/dashboard.md section 8.6): the comfort payment of the SmartObjects.GameConfig singleton, in a
// dialog addressed by `?dialog=liveops-settings`, staged like any edit (the singleton created when the branch has none).
import { useEffect, useMemo, useState } from 'react';
import { ops } from '../../api/operations';
import type { Document, Json, Parameter } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { baseOf } from '../../content/actions';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { createDocumentDraft, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { AlertCircle, Plus, Settings } from '../../ui/icons';
import { Modal, useUrlParam } from '../../ui/Modal';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';
import './liveops.css';

export const GAME_CONFIG = 'SmartObjects.GameConfig';
export const DIALOG_KEY = 'liveops-settings';

const TYPE_WORDS: Record<number, (value: number) => string> = {
  0: (v) => `the last ${v} payment${v === 1 ? '' : 's'}`,
  1: (v) => `the payments of the last ${v} day${v === 1 ? '' : 's'}`,
  2: () => 'all payments',
};

/** Hosts the dialog on every branch page; opens when the URL carries `?dialog=liveops-settings`. */
export function LiveOpsSettingsHost() {
  const param = useUrlParam('dialog');
  if (param.value !== DIALOG_KEY) return null;
  return <LiveOpsSettingsDialog onClose={param.close} />;
}

export function LiveOpsSettingsDialog({ onClose }: { onClose: () => void }) {
  const { workspace, catalog } = useWidgetEnv();
  const version = useWorkspaceVersion(workspace);
  const { client } = useAuth();
  const { org, game, can } = useGame();
  const branch = catalog.branch;
  const template = catalog.templates.get(GAME_CONFIG);
  const [server, setServer] = useState<Document | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    client
      .data(ops.listDocuments, { path: { org, game: game.slug, branch }, query: { template: GAME_CONFIG, limit: 1 } })
      .then((page) => alive && setServer((page.items[0] as Document | undefined) ?? null))
      .catch(() => alive && setServer(null));
    return () => {
      alive = false;
    };
  }, [client, org, game.slug, branch]);
  const draftNew = workspace.all.find((d) => d.kind === 'document' && d.template === GAME_CONFIG && d.status === 'new');
  const doc: Document | null = useMemo(() => {
    if (draftNew) return draftNew.current as unknown as Document;
    if (!server) return null;
    return (workspace.getDraft(entityKey('document', server.id))?.current as unknown as Document | undefined) ?? server;
  }, [draftNew, server, workspace, version]); // eslint-disable-line react-hooks/exhaustive-deps -- the version is the draft's change signal
  const canWrite = can('content_write') && !workspace.isProtected;
  const comfortParam = template?.effectiveParameters.find((p) => p.name === 'comfortPay');
  const inline = comfortParam?.template ? catalog.templates.get(comfortParam.template) : undefined;
  const comfort = (doc?.data as Record<string, Json> | undefined)?.comfortPay;
  const c = (comfort && typeof comfort === 'object' && !Array.isArray(comfort) ? comfort : {}) as Record<string, Json>;
  const type = Number(c.type ?? 0);
  const value = Number(c.value ?? 5);
  const formula = Number(c.formula ?? 0);
  const excluded = Array.isArray(c.exclude) ? c.exclude.length : 0;
  const edit = (field: string, v: Json) => {
    if (!doc) return;
    workspace.edit('document', doc.id, `data.comfortPay.${field}`, v, { base: server && doc.id === server.id ? baseOf(server) : undefined, label: 'LiveOps settings', template: GAME_CONFIG });
  };
  const create = () => {
    if (!template) return;
    createDocumentDraft(workspace, template, { comfortPay: { type: 0, value: 5, formula: 0, exclude: [] } });
  };
  const sentence = `${formula === 0 ? 'the median' : 'the mean'} of ${(TYPE_WORDS[type] ?? TYPE_WORDS[0]!)(value)}${excluded ? `, excluding ${excluded} store item${excluded === 1 ? '' : 's'}` : ''}`;
  const widget = (name: string, label: string, help?: string, className?: string) => {
    const p = inline?.effectiveParameters.find((x) => x.name === name);
    if (!p || !doc) return null;
    const param: Parameter = { ...p, displayName: label };
    return (
      <Field label={label} help={help} className={className}>
        <FieldWidget param={param} value={c[name]} owner={doc.id} disabled={!canWrite} onCommit={(v) => edit(name, v)} />
      </Field>
    );
  };
  return (
    <Modal title="LiveOps settings" open onClose={onClose} size="m" footer={<><span className="spacer" /><Button onClick={onClose}>Close</Button></>}>
      <div className="lo-settings" data-testid="liveops-settings">
        <div className="section-head">
          <h3>Comfort payment</h3>
          <p>The price the player is comfortable with, kept on the profile and read by Comfort conditions. Edits are staged; Save All commits them.</p>
        </div>
        {!template ? (
          <p className="lo-note danger" role="alert">
            <AlertCircle aria-hidden="true" /> The branch has no SmartObjects.GameConfig template.
          </p>
        ) : server === undefined && !draftNew ? (
          <div className="skeleton-rows" role="status" aria-label="Loading the LiveOps settings">
            {[0, 1, 2].map((k) => (
              <div key={k} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        ) : !doc ? (
          <EmptyState
            icon={<Settings aria-hidden="true" />}
            title="No LiveOps settings yet"
            body="Create them to set how the comfortable payment is computed; the branch then stages a SmartObjects.GameConfig document."
            action={
              <Button size="s" variant="primary" onClick={create} disabled={!canWrite} missingPermission={canWrite ? undefined : 'content_write'} icon={<Plus aria-hidden="true" />}>
                Create
              </Button>
            }
          />
        ) : (
          <>
            <p className="lo-sentence" data-testid="comfort-sentence">
              Comfortable payment = <strong>{sentence}</strong>
            </p>
            <div className="form-grid">
              {widget('type', 'Type')}
              {type !== 2 && widget('value', type === 1 ? 'Days' : 'Payments')}
              {widget('formula', 'Formula', 'Mediana = the upper median; Medium = the mean')}
              {widget('exclude', 'Exclude', 'Purchases of these store items do not count', 'span-2')}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
